export interface LogEntry {
    ts?: string;
    level: "INFO" | "WARN" | "ERROR";
    component: string;
    message: string;
    details?: Record<string, unknown>;
}

const iconForLevel: Record<LogEntry["level"], string> = {
    INFO: "🟢",
    WARN: "🟠",
    ERROR: "🔴"
};

const whyNoteForLevel: Record<LogEntry["level"], { why: string; action: string }> = {
    INFO: {
        why: "Informational update—useful for tracing user journeys.",
        action: "No action required unless correlated issues appear."
    },
    WARN: {
        why: "Potential UX hiccup detected; verify if this repeats.",
        action: "Investigate component state and recent changes."
    },
    ERROR: {
        why: "User-facing problem encountered; triage immediately.",
        action: "Capture repro steps, check console stack trace, and file a bug."
    }
};

const formatReport = (entry: Required<LogEntry>): string => {
    const captured = new Date(entry.ts);
    const headerDate = captured.toISOString().split("T")[0];
    const icon = iconForLevel[entry.level];
    const whyBlock = whyNoteForLevel[entry.level];
    const path = typeof window !== "undefined" ? window.location.pathname : "n/a";
    const userAgent = typeof navigator !== "undefined" ? navigator.userAgent : "n/a";
    const detailSummary = entry.details ? JSON.stringify(entry.details, null, 2) : "None provided";

    return [
        `# Frontend Event Report (${headerDate})`,
        "",
        `### ${icon} ${entry.component}`,
        `_Captured ${captured.toUTCString()}_`,
        "",
        `**Outcome:** ${entry.level} from ${entry.component} — ${entry.message}`,
        `**Why this matters:** ${whyBlock.why}`,
        "",
        "**Event facts**",
        `- Page path: ${path}`,
        `- User agent: ${userAgent}`,
        `- Details: ${detailSummary}`,
        "",
        `**Suggested action:** ${whyBlock.action}`,
        "",
        "-----------------------------------------"
    ].join("\n");
};

export const logEvent = (entry: LogEntry) => {
    const payload: Required<LogEntry> = {
        ...entry,
        ts: entry.ts ?? new Date().toISOString(),
        details: entry.details ?? {}
    };
    const report = formatReport(payload);
    console.log(report);
    const buffer = JSON.parse(localStorage.getItem("frontendLogs") ?? "[]");
    buffer.push({ ...payload, report });
    localStorage.setItem("frontendLogs", JSON.stringify(buffer.slice(-200)));
};
